import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AutoComplete } from "antd";
import { Calendar } from "lucide-react";
import { notify } from "@/components/common/Notification";
import { PtsExpenseSummaryService } from "@/services/APIs";
import type {
  ExpenseCurrency,
  ExpenseItem,
} from "@/services/APIs/PtsExpenseSummaryService";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  CURRENCY_OPTIONS,
  DEFAULT_CURRENCY,
  EXPENSE_CATEGORY_OPTIONS,
  EXPENSE_FREQUENCY_OPTIONS,
  EXPENSE_STATUS_OPTIONS,
} from "./constants";
import { currencySymbol, formatMoney, parseAmount, toYmd } from "./utils";

/** The dialog sits at z-[100], so portalled dropdowns must stack above it. */
const SELECT_CONTENT_CLASS = "z-[110] bg-white";

interface FormState {
  title: string;
  category: string;
  vendor: string;
  frequency: string;
  billing_date: string;
  quantity: string;
  currency: ExpenseCurrency;
  unit_cost: string;
  status: string;
  is_recurring: boolean;
}

const emptyForm = (): FormState => ({
  title: "",
  category: "",
  vendor: "",
  frequency: "Monthly",
  billing_date: toYmd(new Date()),
  quantity: "1",
  currency: DEFAULT_CURRENCY,
  unit_cost: "",
  status: "Active",
  is_recurring: false,
});

const toFormState = (expense: ExpenseItem): FormState => ({
  title: expense.title ?? "",
  category: expense.category ?? "",
  vendor: expense.vendor ?? "",
  frequency:
    expense.frequency === "One-Time"
      ? "One Time"
      : (expense.frequency ?? "Monthly"),
  billing_date: String(expense.billing_date ?? "").slice(0, 10),
  quantity: String(parseAmount(expense.quantity)),
  currency: (expense.currency as ExpenseCurrency) ?? DEFAULT_CURRENCY,
  unit_cost: String(parseAmount(expense.unit_cost)),
  status: expense.status ?? "Active",
  is_recurring: expense.is_recurring ?? false,
});

interface AddEditExpenseModalProps {
  open: boolean;
  onClose: () => void;
  expense?: ExpenseItem | null;
  vendorOptions: string[];
}

const AddEditExpenseModal = ({
  open,
  onClose,
  expense,
  vendorOptions,
}: AddEditExpenseModalProps) => {
  const queryClient = useQueryClient();
  const isEditMode = Boolean(expense?.id);
  const [form, setForm] = useState<FormState>(emptyForm);

  useEffect(() => {
    if (!open) {
      return;
    }
    setForm(expense ? toFormState(expense) : emptyForm());
  }, [open, expense]);

  const totalExpense = useMemo(
    () => parseAmount(form.quantity) * parseAmount(form.unit_cost),
    [form.quantity, form.unit_cost]
  );

  const symbol = currencySymbol(form.currency);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title.trim(),
        category: form.category.trim(),
        vendor: form.vendor.trim(),
        frequency: form.frequency,
        billing_date: form.billing_date,
        quantity: parseAmount(form.quantity),
        currency: form.currency,
        unit_cost: parseAmount(form.unit_cost),
        total_expense: totalExpense,
        status: form.status,
        is_recurring: form.is_recurring,
      };

      if (isEditMode && expense) {
        return PtsExpenseSummaryService.updateExpense(expense.id, payload);
      }
      return PtsExpenseSummaryService.createExpense(payload);
    },
    onSuccess: result => {
      if (!result) {
        return;
      }
      notify.success(
        isEditMode
          ? "Expense updated successfully"
          : "Expense added successfully"
      );
      queryClient.invalidateQueries({ queryKey: ["expense-summary"] });
      onClose();
    },
  });

  const validate = (): string | null => {
    if (!form.title.trim()) {
      return "Please enter an expense title";
    }
    if (!form.category.trim()) {
      return "Please select a category";
    }
    if (!form.vendor.trim()) {
      return "Please enter a vendor";
    }
    if (!form.frequency) {
      return "Please select a frequency";
    }
    if (!form.billing_date) {
      return "Please select a billing date";
    }
    if (parseAmount(form.quantity) <= 0) {
      return "Quantity must be greater than zero";
    }
    if (form.unit_cost.trim() === "" || parseAmount(form.unit_cost) < 0) {
      return "Please enter a valid unit cost";
    }
    return null;
  };

  const handleSubmit = () => {
    const error = validate();
    if (error) {
      notify.error(error);
      return;
    }
    saveMutation.mutate();
  };

  const autoCompleteOptions = (values: string[]) =>
    values.map(value => ({ value }));

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-xl flex-col bg-white p-0 sm:w-full">
        <DialogHeader className="flex-shrink-0 px-6 pb-4 pt-6">
          <DialogTitle>
            {isEditMode ? "Edit Expense" : "Add Expense"}
          </DialogTitle>
          <DialogDescription>
            Enter the details — total is calculated automatically
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="expense-title">Title</Label>
            <Input
              id="expense-title"
              value={form.title}
              onChange={event =>
                setForm(prev => ({ ...prev, title: event.target.value }))
              }
              placeholder="e.g. Cursor, AWS, Domain Renewal..."
              className="h-10 bg-white"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>Category</Label>
              <Select
                value={form.category}
                onValueChange={value =>
                  setForm(prev => ({ ...prev, category: value }))
                }
              >
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className={SELECT_CONTENT_CLASS}>
                  {EXPENSE_CATEGORY_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="expense-form-vendor">Vendor</Label>
              <AutoComplete
                id="expense-form-vendor"
                value={form.vendor}
                onChange={value =>
                  setForm(prev => ({ ...prev, vendor: value ?? "" }))
                }
                options={autoCompleteOptions(vendorOptions)}
                filterOption={(input, option) =>
                  String(option?.value ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                placeholder="e.g. Anthropic, Amazon..."
                className="h-10 w-full [&_.ant-select-selector]:!h-10 [&_.ant-select-selector]:!items-center [&_.ant-select-selector]:!rounded-md"
                getPopupContainer={trigger =>
                  trigger.parentElement ?? document.body
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>Frequency</Label>
              <Select
                value={form.frequency}
                onValueChange={value =>
                  setForm(prev => ({ ...prev, frequency: value }))
                }
              >
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent className={SELECT_CONTENT_CLASS}>
                  {EXPENSE_FREQUENCY_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="expense-billing-date">Billing Date</Label>
              <div className="relative">
                <Input
                  id="expense-billing-date"
                  type="date"
                  value={form.billing_date}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      billing_date: event.target.value,
                    }))
                  }
                  className="h-10 bg-white pr-10"
                />
                <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label>Currency</Label>
              <Select
                value={form.currency}
                onValueChange={value =>
                  setForm(prev => ({
                    ...prev,
                    currency: value as ExpenseCurrency,
                  }))
                }
              >
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent className={SELECT_CONTENT_CLASS}>
                  {CURRENCY_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="expense-quantity">Quantity</Label>
              <Input
                id="expense-quantity"
                type="number"
                min={0}
                step="1"
                value={form.quantity}
                onChange={event =>
                  setForm(prev => ({ ...prev, quantity: event.target.value }))
                }
                className="h-10 bg-white"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="expense-unit-cost">{`Unit Cost (${symbol})`}</Label>
              <Input
                id="expense-unit-cost"
                type="number"
                min={0}
                step="0.01"
                value={form.unit_cost}
                onChange={event =>
                  setForm(prev => ({ ...prev, unit_cost: event.target.value }))
                }
                placeholder="0.00"
                className="h-10 bg-white"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md bg-indigo-50 px-4 py-3">
            <span className="text-sm font-medium text-indigo-700">
              Total Expense
            </span>
            <span className="text-sm font-semibold text-indigo-900">
              {formatMoney(totalExpense, form.currency)}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={value =>
                  setForm(prev => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger className="h-10 bg-white">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className={SELECT_CONTENT_CLASS}>
                  {EXPENSE_STATUS_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="expense-recurring">Recurring</Label>
              <div className="flex h-10 items-center justify-between rounded-md border border-gray-200 bg-[#fafafa] px-3.5">
                <span className="text-sm text-gray-500">
                  Is this a recurring expense?
                </span>
                <Switch
                  id="expense-recurring"
                  checked={form.is_recurring}
                  onCheckedChange={checked =>
                    setForm(prev => ({ ...prev, is_recurring: checked }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 flex-col gap-2 border-t px-6 pb-6 pt-4 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={saveMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending
              ? "Saving..."
              : isEditMode
                ? "Update Expense"
                : "Save Expense"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditExpenseModal;
